// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeListItem } from '@/billing/api/types';
import {
  STATUS_CHIP_CONFIG,
  formatChargeTimestamp,
} from '@/billing/components/charge-status-constants';
import { CHARGE_TRIGGER_TYPE_DESCRIPTORS } from '@/billing/components/charge-trigger-type-icons';
import { StatusChip } from '@/components/status-chip';
import { MoreHorizRounded } from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { decorateChargeLabel } from '@/project/utilities/decorate-charge-label';
import { formatShortDate } from '@/utilities/date-time';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { MouseEvent } from 'react';

interface ChargeTableRowProps {
  charge: ChargeListItem;
  onOpenMenu: (event: MouseEvent<HTMLElement>, chargeId: string) => void;
}

export function ChargeTableRow(props: ChargeTableRowProps) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2">
          {props.charge.project.customer?.name ?? '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{props.charge.project.name}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {decorateChargeLabel(
            props.charge.label,
            props.charge.splitIndex,
            props.charge.splitCount
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          <Tooltip title={props.charge.item.name} arrow placement="top">
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                borderBottom: '1px dotted',
                borderColor: 'text.secondary',
              }}
            >
              {props.charge.item.sku}
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {props.charge.pricingMode === 'PER_UNIT' &&
            props.charge.unitPrice !== null
              ? `${formatNumber(props.charge.quantity)} \u00d7 ${formatCurrencyValue(props.charge.unitPrice)}`
              : `${formatCurrencyValue(props.charge.totalPrice)} fixed`}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={500}>
          {formatCurrencyValue(props.charge.totalPrice)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {props.charge.servicePeriodStart && props.charge.servicePeriodEnd
            ? `${formatShortDate(props.charge.servicePeriodStart)} \u2013 ${formatShortDate(props.charge.servicePeriodEnd)}`
            : '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <BillableDateCell charge={props.charge} />
      </TableCell>
      <TableCell>
        {props.charge.status === 'CANCELLED' ? (
          <Tooltip
            arrow
            placement="top"
            title={
              <Stack spacing={0.5} sx={{ py: 0.5 }}>
                {props.charge.cancellationReason ? (
                  <Typography variant="caption">
                    {props.charge.cancellationReason}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    No reason provided
                  </Typography>
                )}
                <Typography variant="caption">
                  Cancelled at{' '}
                  {props.charge.cancelledAt
                    ? formatChargeTimestamp(props.charge.cancelledAt)
                    : '\u2014'}
                </Typography>
              </Stack>
            }
          >
            <Box component="span" sx={{ display: 'inline-block' }}>
              <StatusChip {...STATUS_CHIP_CONFIG[props.charge.status]} />
            </Box>
          </Tooltip>
        ) : (
          <StatusChip {...STATUS_CHIP_CONFIG[props.charge.status]} />
        )}
      </TableCell>
      <TableCell align="right">
        <IconButton
          size="small"
          onClick={(e) => props.onOpenMenu(e, props.charge.id)}
          aria-label="Charge actions"
        >
          <MoreHorizRounded fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

interface BillableDateCellProps {
  charge: ChargeListItem;
}

function BillableDateCell(props: BillableDateCellProps) {
  if (props.charge.status === 'CANCELLED') {
    return (
      <Typography variant="body2" color="text.secondary">
        {'\u2014'}
      </Typography>
    );
  }

  const { expectedBillableDate, actualBillableAt } = props.charge;
  if (!expectedBillableDate && !actualBillableAt) {
    return (
      <Typography variant="body2" color="text.secondary">
        {'\u2014'}
      </Typography>
    );
  }

  const primaryDate =
    actualBillableAt !== null ? actualBillableAt : expectedBillableDate;
  if (primaryDate === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {'\u2014'}
      </Typography>
    );
  }
  const isExpectedOnly = actualBillableAt === null;

  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TriggerTypeIcon chargeSchedule={props.charge.chargeSchedule} />
        <Tooltip
          arrow
          placement="top"
          title={
            <BillableDateTooltipBody
              expectedBillableDate={expectedBillableDate}
              actualBillableAt={actualBillableAt}
            />
          }
        >
          <Typography
            variant="body2"
            component="span"
            color={isExpectedOnly ? 'text.secondary' : 'text.primary'}
            sx={{
              display: 'inline-block',
              borderBottom: '1px dotted',
              borderColor: isExpectedOnly ? 'text.secondary' : 'text.primary',
            }}
          >
            {formatShortDate(primaryDate)}
          </Typography>
        </Tooltip>
      </Stack>
      {isExpectedOnly && (
        <Typography variant="caption" color="text.secondary">
          expected
        </Typography>
      )}
    </Stack>
  );
}

interface TriggerTypeIconProps {
  // The icon reflects the parent line item's schedule type (PERIODIC →
  // clock, MILESTONE → flag), not the per-charge trigger_type. Driving
  // it from the schedule keeps the icon stable across legacy charges
  // whose stored trigger_type is TIME because the row was created
  // before milestone billing landed and the line item was later
  // converted to MILESTONE — those rows correctly belong to a milestone
  // plan today and should render as such.
  chargeSchedule: ChargeListItem['chargeSchedule'];
}

function TriggerTypeIcon(props: TriggerTypeIconProps) {
  const descriptor =
    props.chargeSchedule === 'MILESTONE'
      ? CHARGE_TRIGGER_TYPE_DESCRIPTORS.MANUAL
      : CHARGE_TRIGGER_TYPE_DESCRIPTORS.TIME;
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Stack spacing={0.25} sx={{ py: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>
            {descriptor.label}
          </Typography>
          <Typography variant="caption">{descriptor.description}</Typography>
        </Stack>
      }
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'text.secondary',
        }}
      >
        {descriptor.icon}
      </Box>
    </Tooltip>
  );
}

interface BillableDateTooltipBodyProps {
  expectedBillableDate: DateTime | null;
  actualBillableAt: DateTime | null;
}

function BillableDateTooltipBody(props: BillableDateTooltipBodyProps) {
  return (
    <Stack spacing={0.5} sx={{ py: 0.5 }}>
      <TooltipLabelValue
        label="Expected"
        value={
          props.expectedBillableDate
            ? formatShortDate(props.expectedBillableDate)
            : '\u2014'
        }
      />
      <TooltipLabelValue
        label="Actual"
        value={
          props.actualBillableAt
            ? formatChargeTimestamp(props.actualBillableAt)
            : '\u2014'
        }
      />
    </Stack>
  );
}

interface TooltipLabelValueProps {
  label: string;
  value: string;
}

function TooltipLabelValue(props: TooltipLabelValueProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" sx={{ color: 'grey.400', minWidth: 56 }}>
        {props.label}
      </Typography>
      <Typography variant="caption">{props.value}</Typography>
    </Stack>
  );
}
